// Color palettes / themes. Each theme defines piece colors and UI accents.

import type { PieceType } from '../types';

export interface ThemePalette {
  id: string;
  name: string;
  bg: string;
  bg2: string;
  grid: string;
  gridStrong: string;
  text: string;
  textDim: string;
  accent: string;
  accent2: string;
  danger: string;
  pieces: Record<PieceType, string>;
  garbage: string;
  ghost: string; // overlay alpha applied separately
}

export const PALETTES: ThemePalette[] = [
  {
    // PRISM — Cyberpunk neon. Highest saturation, electric accents.
    id: 'prism',
    name: 'Prism',
    bg: '#08040f',
    bg2: '#170a2e',
    grid: '#221540',
    gridStrong: '#311f5a',
    text: '#f8f0ff',
    textDim: '#a08fcc',
    accent: '#ff2da0',
    accent2: '#00f0ff',
    danger: '#ff3366',
    pieces: {
      I: '#00f0ff',
      O: '#ffd23f',
      T: '#c25eff',
      S: '#5fff8e',
      Z: '#ff3d6c',
      J: '#3d7dff',
      L: '#ff9a33',
    },
    garbage: '#6a5d80',
    ghost: '#ffffff',
  },
  {
    // AMBER CRT — Warm phosphor glow. Pushed peak amber + brighter highlights.
    id: 'amber',
    name: 'Amber CRT',
    bg: '#0e0600',
    bg2: '#1f1100',
    grid: '#2c1a00',
    gridStrong: '#412600',
    text: '#ffe8b0',
    textDim: '#b08840',
    accent: '#ff9520',
    accent2: '#ffd84a',
    danger: '#ff5028',
    pieces: {
      I: '#ffd848',
      O: '#ffaa1f',
      T: '#ff7a1c',
      S: '#ffbc60',
      Z: '#ff6644',
      J: '#ffe48a',
      L: '#ff7e00',
    },
    garbage: '#503010',
    ghost: '#ffe1a0',
  },
  {
    // PHOSPHOR — Mono green CRT. Brighter peaks + slightly more chroma range.
    id: 'mono',
    name: 'Phosphor',
    bg: '#000806',
    bg2: '#001209',
    grid: '#002814',
    gridStrong: '#003c20',
    text: '#b0ffd8',
    textDim: '#3a9866',
    accent: '#4cff80',
    accent2: '#0affb0',
    danger: '#ff4444',
    pieces: {
      I: '#b0ffd8',
      O: '#88ffae',
      T: '#5cfa78',
      S: '#3cf254',
      Z: '#18f034',
      J: '#9affc6',
      L: '#74ff8a',
    },
    garbage: '#1c4030',
    ghost: '#b0ffd8',
  },
  {
    // VAPORWAVE — Hot pink + cyan dream. Push the pink + cyan deeper.
    id: 'vapor',
    name: 'Vaporwave',
    bg: '#0a0322',
    bg2: '#1a083f',
    grid: '#2d125f',
    gridStrong: '#421b78',
    text: '#fff0fa',
    textDim: '#b88adc',
    accent: '#ff4ccc',
    accent2: '#00f4ff',
    danger: '#ff2868',
    pieces: {
      I: '#00f4ff',
      O: '#ffee44',
      T: '#ff4ccc',
      S: '#7fffd4',
      Z: '#ff70d4',
      J: '#5b8cff',
      L: '#ff9c2c',
    },
    garbage: '#62418a',
    ghost: '#fff0fa',
  },
  {
    // CLASSIC — Guideline standard. Slightly punchier saturation.
    id: 'classic',
    name: 'Classic',
    bg: '#080812',
    bg2: '#101022',
    grid: '#1a1a32',
    gridStrong: '#272745',
    text: '#ffffff',
    textDim: '#a4a4c2',
    accent: '#ffcc00',
    accent2: '#00aaff',
    danger: '#ff4040',
    pieces: {
      I: '#00dcec',
      O: '#ffd23f',
      T: '#a83dff',
      S: '#3fde6e',
      Z: '#ff3838',
      J: '#3870ff',
      L: '#ff8c2e',
    },
    garbage: '#6a6a8a',
    ghost: '#ffffff',
  },
  {
    // SUNSET — Warm dusk glow. More coral, more peach, less muted.
    id: 'sunset',
    name: 'Sunset',
    bg: '#160622',
    bg2: '#280930',
    grid: '#3c1444',
    gridStrong: '#502056',
    text: '#fff2ea',
    textDim: '#d09298',
    accent: '#ff488c',
    accent2: '#ffbc4f',
    danger: '#ff3030',
    pieces: {
      I: '#4fd6ff',
      O: '#ffbc4f',
      T: '#ff4cc8',
      S: '#7cffa0',
      Z: '#ff4858',
      J: '#7090ff',
      L: '#ff7438',
    },
    garbage: '#5a3a55',
    ghost: '#fff0e8',
  },
  {
    // AURORA — Cool northern lights. Boosted teal + violet, mid greens.
    id: 'aurora',
    name: 'Aurora',
    bg: '#020a14',
    bg2: '#04141f',
    grid: '#072036',
    gridStrong: '#0a2c4c',
    text: '#e6fcff',
    textDim: '#6ca0b0',
    accent: '#3effc8',
    accent2: '#8a52ff',
    danger: '#ff6868',
    pieces: {
      I: '#48ecff',
      O: '#fff36b',
      T: '#c258ff',
      S: '#3fff9a',
      Z: '#ff4884',
      J: '#5e84ff',
      L: '#ffa64a',
    },
    garbage: '#3a546a',
    ghost: '#e0fcff',
  },
  {
    // ICE — Crystalline cool. Add brighter highlights + a bit of edge color
    // so pieces don't disappear into the pale background.
    id: 'ice',
    name: 'Ice',
    bg: '#010614',
    bg2: '#031232',
    grid: '#0c2046',
    gridStrong: '#10295a',
    text: '#f0f8ff',
    textDim: '#80a2c8',
    accent: '#42d8ff',
    accent2: '#b8d8ff',
    danger: '#ff4858',
    pieces: {
      I: '#5cdcff',
      O: '#e8f4ff',
      T: '#6fa8ff',
      S: '#7af0d4',
      Z: '#ff7088',
      J: '#3a6cff',
      L: '#ffc070',
    },
    garbage: '#3a546a',
    ghost: '#e8f4ff',
  },
  {
    // FOREST — Natural greens + honey gold. Push saturation across.
    id: 'forest',
    name: 'Forest',
    bg: '#040f08',
    bg2: '#091e10',
    grid: '#142e18',
    gridStrong: '#1f4022',
    text: '#eaffe8',
    textDim: '#74a878',
    accent: '#5cff5c',
    accent2: '#ffce38',
    danger: '#ff5050',
    pieces: {
      I: '#3fffd4',
      O: '#ffce38',
      T: '#9a4cff',
      S: '#48ff6a',
      Z: '#ff6648',
      J: '#4a90ff',
      L: '#ff9128',
    },
    garbage: '#3a5a40',
    ghost: '#e8ffe8',
  },
  {
    // INFERNO — Hot fire. Push primary reds darker, accents brighter.
    id: 'inferno',
    name: 'Inferno',
    bg: '#120202',
    bg2: '#240505',
    grid: '#311010',
    gridStrong: '#451414',
    text: '#fff0e8',
    textDim: '#b86460',
    accent: '#ff3030',
    accent2: '#ffb24a',
    danger: '#ff1818',
    pieces: {
      I: '#ffce4a',
      O: '#ff7228',
      T: '#ff3a68',
      S: '#ffae4a',
      Z: '#ff2828',
      J: '#ff6038',
      L: '#ff8418',
    },
    garbage: '#5a2a2a',
    ghost: '#fff0e8',
  },
  {
    // MINIMAL — Pure grayscale with wider piece-value spacing so each
    // tetrimino is distinguishable without breaking the monochrome look.
    // Values fan from ~30% (Z) to 100% (I) lightness; spacing is enough
    // to read piece type even when stamped under the bloom filter.
    id: 'mono-white',
    name: 'Minimal',
    bg: '#0a0a0e',
    bg2: '#12121a',
    grid: '#22222c',
    gridStrong: '#2e2e38',
    text: '#fafafa',
    textDim: '#8a8a96',
    accent: '#fafafa',
    accent2: '#b0b0b8',
    danger: '#ff5050',
    pieces: {
      I: '#ffffff',  // 100%
      L: '#d4d4d4',  // 84%
      O: '#b0b0b0',  // 69%
      T: '#8a8a8a',  // 54%
      S: '#6c6c6c',  // 42%
      J: '#525258',  // 32%
      Z: '#3b3b42',  // 23%
    },
    garbage: '#5a5a64',
    ghost: '#ffffff',
  },
];

/** Returns the palette with the given id from the built-in catalogue OR
 *  the optional list of user-authored custom palettes. Falls back to
 *  Prism (PALETTES[0]) if nothing matches. */
export function getPalette(id: string, customPalettes: ThemePalette[] = []): ThemePalette {
  return PALETTES.find((p) => p.id === id)
    ?? customPalettes.find((p) => p.id === id)
    ?? PALETTES[0]!;
}

/** Auto-derive the secondary palette fields (bg2, grid, gridStrong, text,
 *  textDim, garbage, ghost) from the user's primary choices (bg, accent,
 *  accent2, pieces) so the custom-palette editor can ask for only the
 *  fields a player actually thinks about. */
export function derivePaletteFields(p: Pick<ThemePalette, 'bg' | 'accent' | 'accent2' | 'danger' | 'pieces'>): ThemePalette {
  const id = `custom:${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
  const bgLum = textLuminanceHex(p.bg);
  const lightText = bgLum < 0.5;
  return {
    id,
    name: 'custom',
    bg: p.bg,
    bg2: shiftLightness(p.bg, +0.05),
    grid: shiftLightness(p.bg, +0.10),
    gridStrong: shiftLightness(p.bg, +0.16),
    text: lightText ? '#f8f8fc' : '#0a0a10',
    textDim: lightText ? '#9090a8' : '#5a5a70',
    accent: p.accent,
    accent2: p.accent2,
    danger: p.danger,
    pieces: { ...p.pieces },
    garbage: '#5a5a68',
    ghost: lightText ? '#ffffff' : '#000000',
  };
}

function textLuminanceHex(hex: string): number {
  const v = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return (((v >> 16) & 0xff) * 0.2126 + ((v >> 8) & 0xff) * 0.7152 + (v & 0xff) * 0.0722) / 255;
}

function shiftLightness(hex: string, amt: number): string {
  const v = parseInt(hex.replace('#', '').slice(0, 6), 16);
  const r = (v >> 16) & 0xff, g = (v >> 8) & 0xff, b = v & 0xff;
  const adj = (c: number) => Math.max(0, Math.min(255, Math.round(c + amt * 255)));
  return `#${[adj(r), adj(g), adj(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** Push the active palette's accents onto the document root as CSS custom
 *  properties so the menu chrome (panel borders, focus highlight, toggles,
 *  scrollbar thumb, buttons, etc.) all visually pull from the same theme
 *  the game is using. Keeps the home screen + in-game feel cohesive. */
export function applyPaletteToCss(p: ThemePalette): void {
  const root = document.documentElement;
  root.style.setProperty('--theme-accent', p.accent);
  root.style.setProperty('--theme-accent2', p.accent2);
  root.style.setProperty('--theme-bg', p.bg);
  root.style.setProperty('--theme-bg2', p.bg2);
  root.style.setProperty('--theme-text', p.text);
  root.style.setProperty('--theme-text-dim', p.textDim);
  root.style.setProperty('--theme-danger', p.danger);
  // Translucent panel surfaces derived from the theme bg so the menu
  // panels feel like they belong to the same world as the playfield.
  // Hex → "r, g, b" channel triplet for use in rgba().
  const hexToTriplet = (hex: string): string => {
    const v = parseInt(hex.replace('#', '').slice(0, 6), 16);
    return `${(v >> 16) & 0xff}, ${(v >> 8) & 0xff}, ${v & 0xff}`;
  };
  root.style.setProperty('--theme-accent-rgb', hexToTriplet(p.accent));
  root.style.setProperty('--theme-accent2-rgb', hexToTriplet(p.accent2));
  root.style.setProperty('--theme-bg2-rgb', hexToTriplet(p.bg2));
}
